import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TableColumn } from '@app/shared';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { MaestrosUtilsService } from '../../services/maestros-utils.service';
import {
  NzUploadChangeParam,
  NzUploadFile,
  NzUploadXHRArgs,
} from 'ng-zorro-antd/upload';
import { Subscription } from 'rxjs';
import {
  DrawerEscapeBlockerService,
  ScreenService,
  getBase64,
  getPartnerOptions,
  CompanyService,
} from '@app/core';
import {GlobalValuesService} from '../../../../core/services/global-values.service';

export function hasRequiredValidator(column: TableColumn): boolean {
  return column.validators?.some((validator) => validator.error === 'required') ?? false;
}

@Component({
  selector: 'app-maestros-form-drawer',
  standalone: false,

  templateUrl: './maestros-form-drawer.component.html',
  styleUrl: './maestros-form-drawer.component.less',
})
export class MaestrosFormDrawerComponent implements OnInit, OnDestroy {
  nzData: {
    columnsData: TableColumn[];
    editingElement: any;
    maestroName: string;
  } = inject(NZ_DRAWER_DATA);

  missingData: string | undefined;

  dynamicForm!: FormGroup;

  loading: boolean = false;
  isMobile$;
  avatarUrl: any;

  @Output() createEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateEvent: EventEmitter<any> = new EventEmitter<any>();

  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private drawerRef: NzDrawerRef<string>,
    private ms: MaestrosUtilsService,
    private screenService: ScreenService,
    private cdRef: ChangeDetectorRef,
    private companyService: CompanyService,
    private globalValuesService: GlobalValuesService,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
  ) {
    ms.loading.subscribe((value: boolean) => (this.loading = value));
    this.isMobile$ = screenService.isMobile$;
  }

  ngOnInit(): void {
    this.setForm();
    this.drawerEscapeBlocker.block();
  }

  // Metodo para crear el formulario dinamico
  setForm(): void {
    const formGroupConfig: { [key: string]: any } = {};

    // Se genera la configuracion general del formulario por cada propiedad que se tenga
    this.nzData.columnsData.forEach((column) => {
      // Verificar que pathData existe antes de acceder a sus propiedades
      if (!column.pathData) {
        return;
      }

      if (
        this.nzData.editingElement ||
        (!this.nzData.editingElement && column.pathData.name !== 'id')
      ) {
        // NO bloquear la creación si un select está vacío, solo hacerlo opcional
        if (column.pathData.type === 'select' && !column.optionList?.length) {
          // Marcar como opcional si no hay opciones disponibles
          // Crear el campo sin validación requerida si no hay opciones
          const validators =
            column.validators && column.validators.length > 0
              ? column.validators
                  .filter((v: any) => v.error !== 'required') // Remover required si no hay opciones
                  .map((validator: any) => validator.function)
              : null;
          formGroupConfig[column.pathData.name] = validators
            ? ['', validators]
            : [''];
          return; // Continuar con el siguiente campo
        }

        if (column.pathData.type === 'multi-select') {
          const validators =
            column.validators && column.validators.length > 0
              ? column.validators.map((validator) => validator.function)
              : null;
          formGroupConfig[column.pathData.name] = validators
            ? [[], validators]
            : [[]];
        } else if (
          column.pathData.type === 'select' &&
          column.optionList &&
          column.optionList.length > 0
        ) {
          // Select con opciones disponibles - crear con validaciones normales
          const defaultValue =
            column.defaultValue !== undefined ? column.defaultValue : '';
          const validators =
            column.validators && column.validators.length > 0
              ? column.validators.map((validator) => validator.function)
              : null;
          formGroupConfig[column.pathData.name] = validators
            ? [defaultValue, validators]
            : [defaultValue];
        } else {
          // Manejar diferentes tipos de valores por defecto
          let defaultValue: any;
          if (column.pathData.type === 'boolean') {
            defaultValue =
              column.defaultValue !== undefined ? column.defaultValue : false;
          } else if (
            column.pathData.type === 'number' ||
            column.pathData.type === 'currency' ||
            column.pathData.type === 'money-rate'
          ) {
            // Solo usar null como valor por defecto para baseRate específicamente (Currency maestro)
            // Para otros campos con min validator, usar 0 o el defaultValue
            // No marcar como touched hasta que el usuario interactúe
            const hasMinValidator = column.validators?.some(
              (v: any) => v.error === 'min',
            );
            if (column.pathData.name === 'baseRate') {
              // Para baseRate específicamente, usar null para activar validación y deshabilitar botón
              defaultValue =
                column.defaultValue !== undefined ? column.defaultValue : null;
            } else {
              // Para otros campos numéricos, usar 0 o defaultValue
              defaultValue =
                column.defaultValue !== undefined ? column.defaultValue : 0;
            }
          } else {
            // Para campos readonly como companyId, establecer el valor por defecto si existe
            if (column.readonly && column.pathData.name === 'companyId') {
              const selectedCompanyId =
                this.companyService.selectedCompanyId$.getValue();
              defaultValue = selectedCompanyId || column.defaultValue || '';
            } else {
              defaultValue =
                column.defaultValue !== undefined ? column.defaultValue : '';
            }
          }

          const validators =
            column.validators && column.validators.length > 0
              ? column.validators.map((validator) => validator.function)
              : null;
          formGroupConfig[column.pathData.name] = validators
            ? [defaultValue, validators]
            : [defaultValue];
        }
      }
    });

    // Se crea el formulario con la configuracion anterior
    // Asegurar que siempre se cree un FormGroup, incluso si está vacío
    this.dynamicForm = this.fb.group(formGroupConfig || {});

    // Forzar validación inicial para campos con valores por defecto inválidos
    this.dynamicForm.updateValueAndValidity();

    // Solo marcar como touched el campo baseRate específicamente (para Currency maestro)
    // NO marcar otros campos como touched hasta que el usuario interactúe con ellos
    // Esto evita que aparezcan errores en rojo al crear nuevos elementos
    const baseRateControl = this.dynamicForm.get('baseRate');
    if (baseRateControl) {
      baseRateControl.updateValueAndValidity();
      if (!baseRateControl.valid) {
        baseRateControl.markAsTouched();
      }
    }

    // Si se esta editando se llena el form con los datos actuales
    if (this.nzData.editingElement) {
      const formValue: any = { ...this.nzData.editingElement };

      // Mapear taxes -> taxIds y supplierTaxes -> supplierTaxIds para productos
      if (Array.isArray(formValue.taxes) && !formValue.taxIds) {
        // Si taxes contiene objetos, extraer los IDs
        if (
          formValue.taxes.length > 0 &&
          typeof formValue.taxes[0] === 'object'
        ) {
          formValue.taxIds = formValue.taxes.map((tax: any) => tax.id);
        } else {
          formValue.taxIds = formValue.taxes;
        }
      }
      if (Array.isArray(formValue.supplierTaxes) && !formValue.supplierTaxIds) {
        // Si supplierTaxes contiene objetos, extraer los IDs
        if (
          formValue.supplierTaxes.length > 0 &&
          typeof formValue.supplierTaxes[0] === 'object'
        ) {
          formValue.supplierTaxIds = formValue.supplierTaxes.map(
            (tax: any) => tax.id,
          );
        } else {
          formValue.supplierTaxIds = formValue.supplierTaxes;
        }
      }

      // Extraer baseRate de currentCurrencyExchange si existe
      if (
        formValue.currentCurrencyExchange?.baseRate !== undefined &&
        !formValue.baseRate
      ) {
        formValue.baseRate = formValue.currentCurrencyExchange.baseRate;
      }

      // Mapear 'members' del backend a 'memberList' si existe
      if (formValue['members'] && !formValue['memberList']) {
        formValue['memberList'] = formValue['members'];
      }

      if (this.nzData.maestroName === 'Equipos de ventas') {
        this.normalizeSalesTeamFormValue(formValue);
      }

      // Convertir arrays de IDs o objetos a objetos para campos multi-select
      this.nzData.columnsData.forEach((column) => {
        if (column.pathData?.type === 'multi-select') {
          const fieldName = column.pathData.name;
          const value = formValue[fieldName];

          if (Array.isArray(value) && value.length > 0) {
            // Caso especial: memberList viene como 'members' del backend con objetos que tienen userId
            // Convertir userId a partnerId primero, igual que companies que viene con id
            if (
              fieldName === 'memberList' &&
              typeof value[0] === 'object' &&
              value[0] !== null &&
              'userId' in value[0]
            ) {
              const users = this.globalValuesService.users$.value || [];
              // Extraer los partnerIds de los userIds, igual que se hace con companies
              const partnerIds: number[] = value
                .map((member: any) => {
                  if (!member?.userId) return null;
                  const user = users.find((u: any) => u.id === member.userId);
                  return user?.partner?.id || null;
                })
                .filter((id: number | null): id is number => id !== null);

              // Ahora convertir los IDs a objetos igual que companies
              if (
                column.optionList &&
                column.optionList.length > 0 &&
                partnerIds.length > 0
              ) {
                formValue[fieldName] = partnerIds
                  .map((id: number) =>
                    column.optionList!.find((opt: any) => opt.id === id),
                  )
                  .filter((opt: any) => opt !== undefined);
              } else {
                formValue[fieldName] = [];
              }
            }
            // Si hay optionList disponibles, convertir a objetos (igual que companies)
            else if (column.optionList && column.optionList.length > 0) {
              // Si los valores son números (IDs), convertirlos a objetos
              if (typeof value[0] === 'number') {
                formValue[fieldName] = value
                  .map((id: number) =>
                    column.optionList!.find((opt: any) => opt.id === id),
                  )
                  .filter((opt: any) => opt !== undefined);
              }
              // Si los valores son objetos con id, extraer solo los objetos que coincidan con optionList
              else if (
                typeof value[0] === 'object' &&
                value[0] !== null &&
                'id' in value[0]
              ) {
                formValue[fieldName] = value
                  .map((obj: any) =>
                    column.optionList!.find((opt: any) => opt.id === obj.id),
                  )
                  .filter((opt: any) => opt !== undefined);
              }
            } else {
              // Si no hay optionList, pero los valores son objetos con id, extraer los IDs
              if (
                typeof value[0] === 'object' &&
                value[0] !== null &&
                'id' in value[0]
              ) {
                formValue[fieldName] = value.map((obj: any) => obj.id);
              }
              // Si ya son números, mantenerlos como están
              else if (typeof value[0] === 'number') {
                // Ya están en el formato correcto
              }
            }
          }
        }
      });

      // Manejar campos con nestedPath: extraer el valor anidado SOLO para campos de tipo 'text'
      this.nzData.columnsData.forEach((column) => {
        // Solo procesar campos de tipo 'text' con nestedPath
        // Los campos 'select' y 'avatar-select' esperan IDs u objetos de optionList, no valores anidados
        if (
          column.pathData?.nestedPath &&
          column.pathData?.name &&
          column.pathData?.type === 'text'
        ) {
          const fieldName = column.pathData.name;
          const nestedPath = column.pathData.nestedPath;

          // Si el valor es un objeto, extraer el valor anidado
          if (
            formValue[fieldName] &&
            typeof formValue[fieldName] === 'object'
          ) {
            const nestedValue = this.getNestedValue(
              formValue[fieldName],
              nestedPath,
            );
            if (nestedValue !== null && nestedValue !== undefined) {
              formValue[fieldName] = nestedValue;
            } else {
              // Si no se puede extraer, usar string vacío
              formValue[fieldName] = '';
            }
          }
        }
      });

      this.dynamicForm.patchValue(formValue);
      this.dynamicForm.updateValueAndValidity({ emitEvent: false });

      // Buscar dinámicamente el campo de tipo 'image' y cargar su valor existente
      const imageColumn = this.nzData.columnsData.find(
        (col) => col.pathData?.type === 'image',
      );
      if (imageColumn?.pathData?.name) {
        const imageFieldName = imageColumn.pathData.name;
        const existingImageUrl = this.nzData.editingElement[imageFieldName];
        if (existingImageUrl) {
          this.avatarUrl = existingImageUrl;
        }
      }
    }

    // Establecer la compañía seleccionada para campos readonly
    this.nzData.columnsData.forEach((column) => {
      if (column.readonly && column.pathData?.name === 'companyId') {
        const selectedCompanyId =
          this.companyService.selectedCompanyId$.getValue();
        if (selectedCompanyId) {
          const control = this.dynamicForm.get('companyId');
          if (control) {
            control.setValue(selectedCompanyId);
            // Usar updateValueAndValidity para asegurar que la validación se actualice
            control.updateValueAndValidity();
            control.disable();
          }
        } else if (column.defaultValue) {
          // Si hay un defaultValue, usarlo
          const control = this.dynamicForm.get('companyId');
          if (control) {
            control.setValue(column.defaultValue);
            control.updateValueAndValidity();
            control.disable();
          }
        }
      }
    });

    // Suscribirse a cambios en amountType para actualizar la vista del campo amount
    const amountTypeControl = this.dynamicForm.get('amountType');
    if (amountTypeControl) {
      amountTypeControl.valueChanges.subscribe(() => {
        this.cdRef.markForCheck();
      });
    }

    // Validación para Etapas: si canCreateQuote es true, completed debe ser false
    // y viceversa
    if (this.nzData.maestroName === 'Etapas') {
      const canCreateQuoteControl = this.dynamicForm.get('canCreateQuote');
      const completedControl = this.dynamicForm.get('completed');

      if (canCreateQuoteControl) {
        canCreateQuoteControl.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((value: boolean) => {
            if (value === true && completedControl) {
              // Si se marca como cotización, desmarcar completada
              completedControl.setValue(false, { emitEvent: false });
            }
          });
      }

      if (completedControl) {
        completedControl.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((value: boolean) => {
            if (value === true && canCreateQuoteControl) {
              // Si se marca como completada, desmarcar cotización
              canCreateQuoteControl.setValue(false, { emitEvent: false });
            }
          });
      }
    }

    if (this.nzData.maestroName === 'Equipos de ventas') {
      this.syncSalesTeamMembersWithManager();
    }
  }

  private normalizeSalesTeamFormValue(formValue: any): void {
    if (formValue.manager?.partner?.id) {
      formValue.managerId = formValue.manager.partner.id;
      return;
    }

    const managerColumn = this.nzData.columnsData.find(
      (column) => column.pathData?.name === 'managerId',
    );
    const managerId = formValue.managerId;
    const managerIsAlreadyPartner = managerColumn?.optionList?.some(
      (option: any) => String(option.id) === String(managerId),
    );

    if (!managerId || managerIsAlreadyPartner) {
      return;
    }

    const users = this.globalValuesService.users$.value || [];
    const managerUser = users.find((user: any) => String(user.id) === String(managerId));
    if (managerUser?.partner?.id) {
      formValue.managerId = managerUser.partner.id;
    }
  }

  private syncSalesTeamMembersWithManager(): void {
    const managerControl = this.dynamicForm.get('managerId');
    const membersControl = this.dynamicForm.get('memberList');

    if (!managerControl || !membersControl) {
      return;
    }

    managerControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((managerId: number | null) => {
        const members = membersControl.value;
        if (!managerId || !Array.isArray(members)) {
          return;
        }

        const filteredMembers = members.filter((member: any) => {
          const memberId = typeof member === 'object' ? member?.id : member;
          return String(memberId) !== String(managerId);
        });

        if (filteredMembers.length !== members.length) {
          membersControl.setValue(filteredMembers, { emitEvent: false });
          membersControl.updateValueAndValidity({ emitEvent: false });
        }
      });
  }

  close(): void {
    this.drawerRef.close();
  }

  onSubmit(): void {
    // Habilitar temporalmente los campos readonly para incluirlos en el payload
    this.nzData.columnsData.forEach((column) => {
      if (column.readonly && column.pathData) {
        const control = this.dynamicForm.get(column.pathData.name);
        if (control && control.disabled) {
          control.enable();
        }
      }
    });

    const formValue = this.dynamicForm.getRawValue();

    // Convertir objetos de multi-select a arrays de IDs
    this.nzData.columnsData.forEach((column) => {
      if (column.pathData?.type === 'multi-select') {
        const fieldName = column.pathData.name;
        const value = formValue[fieldName];
        if (Array.isArray(value) && value.length > 0) {
          // Si los valores son objetos, extraer solo los IDs
          if (
            typeof value[0] === 'object' &&
            value[0] !== null &&
            'id' in value[0]
          ) {
            formValue[fieldName] = value.map((item: any) => item.id);
          }
        } else if (!value) {
          formValue[fieldName] = [];
        }
      }
    });

    // Eliminar campos taxes y supplierTaxes si existen (solo se envían taxIds y supplierTaxIds)
    if (formValue.taxes) {
      delete formValue.taxes;
    }
    if (formValue.supplierTaxes) {
      delete formValue.supplierTaxes;
    }

    // Detectar si hay campos de tipo 'image' (aunque no tengan archivo nuevo)
    const hasImageField = this.nzData.columnsData.some((column) => {
      return column.pathData?.type === 'image';
    });

    // Si hay campos de tipo 'image', SIEMPRE usar FormData
    if (hasImageField) {
      const formData = new FormData();

      // Agregar todos los campos al FormData
      Object.keys(formValue).forEach((key) => {
        const value = formValue[key];
        const column = this.nzData.columnsData.find(
          (col) => col.pathData?.name === key,
        );

        if (column?.pathData?.type === 'image') {
          // Si hay un archivo nuevo, agregarlo
          if (value instanceof File) {
            formData.append(key, value, value.name);
          }
          // Si no hay archivo nuevo pero estamos editando, no agregar nada (el backend mantendrá la imagen existente)
          // Si estamos creando y no hay archivo, tampoco agregar nada
        } else if (value !== null && value !== undefined) {
          // Para otros tipos, convertir a string si es necesario
          if (typeof value === 'boolean') {
            formData.append(key, value ? 'true' : 'false');
          } else if (Array.isArray(value)) {
            // Para arrays, enviar como JSON string o como múltiples valores
            if (value.length > 0 && typeof value[0] === 'object') {
              formData.append(key, JSON.stringify(value));
            } else {
              value.forEach((item) => formData.append(key, String(item)));
            }
          } else if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      // Volver a deshabilitar los campos readonly
      this.nzData.columnsData.forEach((column) => {
        if (column.readonly && column.pathData) {
          const control = this.dynamicForm.get(column.pathData.name);
          if (control) {
            control.disable();
          }
        }
      });

      if (this.nzData.editingElement) {
        // Asegurar que el id está incluido en el FormData
        if (!formData.has('id') && this.nzData.editingElement.id) {
          formData.append('id', String(this.nzData.editingElement.id));
        }
        this.updateEvent.emit(formData);
      } else {
        this.createEvent.emit(formData);
      }
    } else {
      // Si no hay campos de tipo 'image', enviar como objeto JSON normal
      // Volver a deshabilitar los campos readonly
      this.nzData.columnsData.forEach((column) => {
        if (column.readonly && column.pathData) {
          const control = this.dynamicForm.get(column.pathData.name);
          if (control) {
            control.disable();
          }
        }
      });

      if (this.nzData.editingElement) {
        // Asegurar que el id está incluido en el payload
        if (!formValue.id && this.nzData.editingElement.id) {
          formValue.id = this.nzData.editingElement.id;
        }
        this.updateEvent.emit(formValue);
      } else {
        this.createEvent.emit(formValue);
      }
    }

    // Cerrar el drawer inmediatamente después de guardar
    this.drawerRef.close();
  }

  hasRequiredValidator(column: TableColumn): boolean {
    return hasRequiredValidator(column);
  }

  getValidatorError(column: TableColumn): string | undefined {
    if (!column.pathData) return undefined;
    const control = this.dynamicForm.get(column.pathData.name);
    if (
      control &&
      control?.errors &&
      column.validators &&
      column.validators.length > 0
    ) {
      return Object.keys(control.errors)
        .map(
          (key) =>
            column.validators!.find((validator) => validator.error === key)!
              .message,
        )
        .join(', ');
    }
    return '';
  }

  isFormValid(): boolean {
    // Verificar validez del formulario usando getRawValue para incluir campos deshabilitados
    // Primero verificamos si el formulario es válido normalmente
    if (this.dynamicForm.valid) {
      return true;
    }

    // Si no es válido, verificamos cada control individualmente
    let isValid = true;

    this.nzData.columnsData.forEach((column) => {
      if (column.pathData?.name !== 'id' && column.noVisibleInForm !== false) {
        // Verificar que pathData existe antes de continuar
        if (!column.pathData) {
          return;
        }

        const control = this.dynamicForm.get(column.pathData.name);
        if (control) {
          // Si el control está deshabilitado, habilitarlo temporalmente para validar
          const wasDisabled = control.disabled;
          if (wasDisabled) {
            control.enable({ emitEvent: false });
          }

          // Verificar si el control es válido
          control.updateValueAndValidity({ emitEvent: false });
          if (!control.valid) {
            isValid = false;
          }

          // Restaurar el estado original
          if (wasDisabled) {
            control.disable({ emitEvent: false });
          }
        }
      }
    });

    return isValid;
  }

  // Metodo para prevenir el error en el navegador de una peticion erronea del nz-upload
  customUpload = (item: NzUploadXHRArgs): Subscription => {
    // Simula un retardo (opcional)
    setTimeout(() => {
      // Llama al callback de éxito para que nz-upload marque el archivo como subido
      item.onSuccess!({}, item.file, null);
    }, 0);
    // Retorna una suscripción vacía, ya que no se está haciendo una petición real
    return new Subscription();
  };

  handleUploadChange(info: NzUploadChangeParam, column: TableColumn): void {
    if (!column.pathData) return;
    const file: NzUploadFile | undefined =
      info.fileList[info.fileList.length - 1];
    if (file) {
      const control = this.dynamicForm.get(column.pathData.name);
      if (control) {
        control.setValue(file.originFileObj as File);
        getBase64(info.file!.originFileObj!, (img: string) => {
          this.loading = false;
          this.avatarUrl = img;
          this.cdRef.detectChanges();
        });
      }
    }
  }

  compareFn = (o1: any, o2: any) => (o1 && o2 ? o1.id === o2.id : o1 === o2);

  // Función para obtener el label de una opción en multi-select
  getOptionLabel(option: any, column: TableColumn): string {
    if (!option) return '';

    // Si tiene nestedPath, intentar obtener el valor anidado
    if (column.pathData?.nestedPath) {
      const nestedValue = this.getNestedValue(
        option,
        column.pathData.nestedPath,
      );
      if (nestedValue) return nestedValue;
    }

    // Si es un user, usar email o partner name
    if (option.email && option.partner) {
      return (
        option.partner.name +
        (option.partner.surName ? ' ' + option.partner.surName : '') +
        ' (' +
        option.email +
        ')'
      );
    } else if (option.email) {
      return option.email;
    }

    // Por defecto, usar name si existe
    return option.name || option.id || '';
  }

  // Función auxiliar para obtener valores anidados
  private getNestedValue(obj: any, path: string): string | null {
    if (!obj || !path) return null;
    const keys = path.split('.');
    let value = obj;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return null;
      }
    }
    return value || null;
  }

  shouldShowPercent(): boolean {
    const amountTypeControl = this.dynamicForm?.get('amountType');
    if (!amountTypeControl) return false;
    const amountType = amountTypeControl.value;
    // Verificar si es "Porcentaje" (español) o "Percent" (inglés)
    return amountType === 'Porcentaje' || amountType === 'Percent';
  }

  // Obtener opciones filtradas para memberList excluyendo al líder actual
  getFilteredMemberListOptions(column: TableColumn): any[] {
    if (column.pathData?.name !== 'memberList' || !column.optionList) {
      return column.optionList || [];
    }

    const selectedManagerId = this.dynamicForm?.get('managerId')?.value;
    if (!selectedManagerId) {
      return column.optionList;
    }

    // Filtrar las opciones excluyendo el lider seleccionado actualmente
    return column.optionList.filter(
      (option: any) => String(option.id) !== String(selectedManagerId),
    );
  }

  ngOnDestroy(): void {
    this.drawerEscapeBlocker.unblock();
  }

  protected readonly getPartnerOptions = getPartnerOptions;
}
